import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface StatItem {
  value: number;
  suffix: string;
  label: string;
  icon: string;
  color: string;
}

const CountUp = ({ target, suffix }: { target: number; suffix: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1800;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const TrustSection = () => {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [content, setContent] = useState({
    badge: '',
    title: '',
    platforms: '',
    stats: [
      { value: 50, suffix: '+', label: '', icon: 'ri-graduation-cap-line', color: '#8c52ff' },
      { value: 200, suffix: '+', label: '', icon: 'ri-briefcase-line', color: '#00c2ff' },
      { value: 8, suffix: '+', label: '', icon: 'ri-award-line', color: '#FF2DAA' },
      { value: 100, suffix: '%', label: '', icon: 'ri-heart-line', color: '#8c52ff' },
    ]
  });

  useEffect(() => {
    // Cargar traducciones por defecto
    setContent({
      badge: t('home.trust.badge'),
      title: t('home.trust.title'),
      platforms: t('home.trust.platforms'),
      stats: [
        { value: 50, suffix: '+', label: t('home.trust.stats.students'), icon: 'ri-graduation-cap-line', color: '#8c52ff' },
        { value: 200, suffix: '+', label: t('home.trust.stats.clients'), icon: 'ri-briefcase-line', color: '#00c2ff' },
        { value: 8, suffix: '+', label: t('home.trust.stats.years'), icon: 'ri-award-line', color: '#FF2DAA' },
        { value: 100, suffix: '%', label: t('home.trust.stats.satisfaction'), icon: 'ri-heart-line', color: '#8c52ff' },
      ]
    });

    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'landing_content', 'trust');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setContent(docSnap.data() as typeof content);
        }
      } catch (error) {
        console.error('Error fetching trust content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [t]);

  const handleSave = async () => {
    try {
      await setDoc(doc(db, 'landing_content', 'trust'), content);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving trust content:', error);
      alert('Error al guardar los cambios.');
    }
  };

  return (
    <section className="relative py-20 bg-[#050008] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(140,82,255,0.12)_0%,transparent_70%)] pointer-events-none" />

      {/* Botones de Control para Admin */}
      {isAdmin && (
        <div className="absolute top-20 right-4 z-50 flex gap-2">
          {!isEditing ? (
            <button 
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
            >
              <i className="ri-edit-line" />
              Editar Sección
            </button>
          ) : (
            <>
              <button 
                onClick={handleSave}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
              >
                <i className="ri-save-line" />
                Guardar
              </button>
              <button 
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
              >
                <i className="ri-close-line" />
                Cancelar
              </button>
            </>
          )}
        </div>
      )}

      {isEditing && (
        <div className="absolute top-32 right-4 z-50">
          <input 
            type="text" 
            placeholder="Escribe aquí para probar..." 
            className="bg-white text-black px-4 py-2 rounded-lg border-2 border-red-500"
          />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          {isEditing ? (
            <div className="max-w-xl mx-auto space-y-3">
              <input 
                type="text" 
                value={content.badge} 
                onChange={(e) => setContent({...content, badge: e.target.value})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-1 text-[#8c52ff] text-xs font-bold uppercase text-center focus:outline-none"
              />
              <input 
                type="text" 
                value={content.title} 
                onChange={(e) => setContent({...content, title: e.target.value})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-3xl font-black text-center focus:outline-none"
              />
            </div>
          ) : (
            <>
              <p className="text-[#8c52ff] text-xs font-bold uppercase tracking-widest mb-3">{content.badge}</p>
              <h2 className="text-3xl md:text-4xl font-black text-white">
                {content.title}
              </h2>
            </>
          )}
          <div className="w-20 h-1 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] rounded-full mx-auto mt-4" />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {content.stats.map((stat, index) => (
            <div
              key={index}
              className="group relative bg-white/3 backdrop-blur-sm border border-white/8 rounded-2xl p-6 text-center hover:border-white/20 transition-all duration-500 hover:-translate-y-1 overflow-hidden"
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `radial-gradient(ellipse at 50% 0%, ${stat.color}15 0%, transparent 70%)` }}
              />
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4"
                style={{ background: `${stat.color}20`, border: `1px solid ${stat.color}30` }}
              >
                <i className={`${stat.icon} text-2xl`} style={{ color: stat.color }} />
              </div>
              
              {isEditing ? (
                <div className="space-y-2">
                  <div className="flex justify-center gap-1">
                    <input 
                      type="text" 
                      value={stat.value} 
                      onChange={(e) => {
                        const newStats = content.stats.map((s, i) => 
                          i === index ? { ...s, value: e.target.value } : s
                        );
                        setContent({...content, stats: newStats});
                      }}
                      className="w-20 bg-white/5 border border-purple-500/50 rounded-lg px-2 py-1 text-white text-2xl font-black text-center focus:outline-none"
                    />
                    <input 
                      type="text" 
                      value={stat.suffix} 
                      onChange={(e) => {
                        const newStats = content.stats.map((s, i) => 
                          i === index ? { ...s, suffix: e.target.value } : s
                        );
                        setContent({...content, stats: newStats});
                      }}
                      className="w-10 bg-white/5 border border-purple-500/50 rounded-lg px-2 py-1 text-white text-2xl font-black text-center focus:outline-none"
                    />
                  </div>
                  <input 
                    type="text" 
                    value={stat.label} 
                    onChange={(e) => {
                      const newStats = content.stats.map((s, i) => 
                        i === index ? { ...s, label: e.target.value } : s
                      );
                      setContent({...content, stats: newStats});
                    }}
                    className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-2 py-1 text-white/50 text-sm font-medium text-center focus:outline-none"
                  />
                </div>
              ) : (
                <>
                  <div
                    className="text-4xl md:text-5xl font-black mb-2"
                    style={{ background: `linear-gradient(135deg, ${stat.color}, #ffffff)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                  >
                    <CountUp target={stat.value} suffix={stat.suffix} />
                  </div>
                  <p className="text-white/50 text-sm font-medium">{stat.label}</p>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Brand logos strip */}
        <div className="mt-16 text-center">
          {isEditing ? (
            <input 
              type="text" 
              value={content.platforms} 
              onChange={(e) => setContent({...content, platforms: e.target.value})}
              className="bg-white/5 border border-purple-500/50 rounded-lg px-4 py-1 text-white/30 text-xs uppercase text-center focus:outline-none mb-6"
            />
          ) : (
            <p className="text-white/30 text-xs uppercase tracking-widest mb-6">{content.platforms}</p>
          )}
          
          <div className="flex flex-wrap items-center justify-center gap-8">
            {[
              { icon: 'ri-instagram-line', label: 'Instagram' },
              { icon: 'ri-facebook-circle-line', label: 'Facebook' },
              { icon: 'ri-tiktok-line', label: 'TikTok' },
              { icon: 'ri-youtube-line', label: 'YouTube' },
              { icon: 'ri-linkedin-box-line', label: 'LinkedIn' },
              { icon: 'ri-mail-send-line', label: 'Email Mktg' },
              { icon: 'ri-robot-line', label: 'AI Tools' },
            ].map((p) => (
              <div key={p.label} className="flex flex-col items-center gap-2 opacity-40 hover:opacity-80 transition-opacity duration-300">
                <i className={`${p.icon} text-3xl text-white`} />
                <span className="text-white/60 text-xs">{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
